import { sleep } from '@m43/core';
import {
    createUseStream,
    SocketListener,
    StreamListener,
    createUseSocketStatus,
} from '@m43/sockets-mark43';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    AdditionalInfoFormViewNext,
    AdditionalInfoType,
    AdditionalInfoView,
    StreamMap,
} from '../../types';
import { AdditionalInfoStore } from './additional-info-store';
import {
    AdditionalInfoNotificationManager,
    ListDirection,
    SortDirection,
    UnreadInfo,
} from './types';
import { useAdditionalInfosResource } from './use-additional-infos-event-resource';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useAdditionalInfosBoloResource } from './use-additional-infos-bolo-resource';
import { autorun, toJS } from 'mobx';
import { getOriginallyAppliedFilters } from './helpers';
import { createEventController } from './controllers/create-event-controller';
import { createScheduledEventController } from './controllers/create-scheduled-event-controller';
import { createBoloController } from './controllers/create-bolo-controller';
// import { createMockPinnedEntries } from '../../helpers/mock-pinned-entries';

const useStream = createUseStream<StreamMap>();

const useSocketStatus = createUseSocketStatus<StreamMap>();

const { DESC } = SortDirection;

/** Delay before clearing unread indicators after infos become visible. */
const MARK_AS_READ_DELAY_MS = 3000;

/** Default number of additional infos to fetch per page. */
const DEFAULT_PAGE_SIZE = 100;

const getListDirection = (_sortDirection?: SortDirection): ListDirection => ListDirection.HISTORIC;

export type UseAdditionalInfosParams = {
    entityId: number;
    entityNumber: string;
    isBolo?: boolean;
    eventOptions?: {
        includeEventNarratives?: boolean;
        isScheduledEvent?: boolean;
    };
    currentUserId: number;
    firstUnreadInfo?: UnreadInfo;
    initialFilter?: Array<AdditionalInfoType>;
    initialSortDirection?: SortDirection;

    onAllRead?: (id: number) => void;
    pageSize?: number;
    notificationManager?: AdditionalInfoNotificationManager;
    crossClassEventsIds?: number[];
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useIntegratedAdditionalInfosStore = ({
    entityId,
    entityNumber,
    currentUserId,
    firstUnreadInfo,
    eventOptions,
    initialFilter,
    initialSortDirection = DESC,
    onAllRead,
    pageSize = DEFAULT_PAGE_SIZE,
    notificationManager,
    isBolo,
    crossClassEventsIds = [],
}: UseAdditionalInfosParams) => {
    const includeEventNarratives = eventOptions?.includeEventNarratives ?? true;
    const isScheduledEvent = eventOptions?.isScheduledEvent ?? false;
    const {
        rollOutCadNarrativeEndpointEnabled,
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
    } = useEnabledFeatures();
    const noSystemMessagesByDefault =
        useEnabledFeatures()?.['filterOutSystemMessagesFromNarrativeByDefault'] ?? true;

    const storeRef = useRef<AdditionalInfoStore>();

    if (storeRef.current === undefined) {
        storeRef.current = new AdditionalInfoStore({
            filter: getOriginallyAppliedFilters(initialFilter, noSystemMessagesByDefault),
            firstUnreadInfo,
            sortDirection: initialSortDirection,
            onAllRead: () => onAllRead?.(entityId),
        });
    }

    const store = storeRef.current;

    // Resource hooks called unconditionally (Rules of Hooks)
    const eventResource = useAdditionalInfosResource(rollOutCadNarrativeEndpointEnabled);
    const boloResource = useAdditionalInfosBoloResource();

    // Controller is a plain strategy object, not a hook — safe to select conditionally via useMemo
    const controller = useMemo(() => {
        if (isBolo) {
            return createBoloController({
                entityId,
                entityNumber,
                resource: boloResource,
                pageSize,
            });
        }
        if (isScheduledEvent) {
            return createScheduledEventController({
                entityId,
                resource: eventResource,
                pageSize,
            });
        }
        return createEventController({
            entityId,
            resource: eventResource,
            pageSize,
            includeEventNarratives,
        });
    }, [
        isBolo,
        isScheduledEvent,
        entityId,
        entityNumber,
        eventResource,
        boloResource,
        pageSize,
        includeEventNarratives,
    ]);

    const infosListenerRef = useRef<StreamListener<StreamMap, 'infos'[]>>();
    const boloInfosListenerRef = useRef<StreamListener<StreamMap, 'boloAdditionalInfo'[]>>();

    const loadPage = useCallback(
        async ({
            cursor,
            listDirection = getListDirection(store.sortDirection),
        }: { cursor?: number; listDirection?: ListDirection } = {}) => {
            const data = await controller.loadPage({
                cursor,
                listDirection,
                filter: toJS(store.filter),
            });
            store.upsertInfos(data.additionalInfos);
            store.upsertAgencyEventNumberToIdMap(data.agencyEventNumberToIdMap);
            return data;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [controller, store]
    );

    const reloadPage = useCallback(async () => {
        store.toggleIsPageReloading();
        store.listStore.setItems([]);
        await loadPage();
        store.toggleIsPageReloading();
    }, [store, loadPage]);

    const retryPageFetch = (): void => {
        store.setPageFetchFailed(false);
    };

    const setSortDirection = useCallback(
        (sortDirection: SortDirection) => {
            store.setSortDirection(sortDirection);
            fetchNextPage({ reset: true });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    const setFilter = useCallback(
        (filter: AdditionalInfoType[]) => {
            store.setFilter(filter);
            fetchNextPage({ reset: true });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    /**
     * Deletes an Additional Info.
     *
     * @param infoId - ID of the Additional Infos to delete.
     * @returns No value returned.
     * @throws An error if the Additional Info delete failed.
     */
    const deleteInfo = useCallback(
        async (infoId: number) => {
            try {
                await controller.deleteInfo(infoId);
                store.removeInfos([infoId]);
            } catch (error) {
                throw new Error(`Error while trying to delete Additional Info id=${infoId}`);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [controller, store]
    );

    const createAdditionalInfo = useCallback(
        async (info: AdditionalInfoFormViewNext): Promise<AdditionalInfoView> => {
            const infosToUpsert = await controller.createInfo(info);
            store.upsertInfos(infosToUpsert);
            return infosToUpsert[0];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [controller, store]
    );

    // Start of infinite scroll logic

    const fetchNextPage = useCallback(
        async ({ reset = false, cursor = store.nextPageCursor } = {}) => {
            store.setIsFetchingNextPage(true);
            const shouldReset = !cursor || reset;

            if (shouldReset) store.reset();
            try {
                const { pagination, additionalInfos } = shouldReset
                    ? await loadPage()
                    : await loadPage({ cursor });

                store.setPagination(pagination);

                if (additionalInfos.length) {
                    store.setNextPageCursor(additionalInfos[additionalInfos.length - 1].id);
                }

                store.setPageFetchFailed(false);
            } catch (e) {
                // A failed next page load is caught and the pageFetchFailed boolean is set to
                // true. Set to true this boolean stops retrying of the fetch. Setting it to false
                // causes the fetch to try again.
                store.setPageFetchFailed(true);
            } finally {
                store.setIsFetchingNextPage(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [loadPage, store]
    );

    // End of infinite scroll logic

    // Start Realtime Update logic

    const updateStreamListeners = useCallback(() => {
        const listeners = controller.createStreamListeners({
            store,
            currentUserId,
            fetchNextPage,
        });
        infosListenerRef.current = listeners.infos;
        boloInfosListenerRef.current = listeners.boloAdditionalInfo;
    }, [controller, store, currentUserId, fetchNextPage]);

    useEffect(
        function reinitializeOnEntityChange() {
            reloadPage();
            updateStreamListeners();
            store.setNextPageCursor(0);
            store.setHasNextPage(true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [entityId]
    );

    useEffect(
        function updateFilterDirective() {
            return autorun(() => {
                store.listStore.setFilterDirective(
                    controller.createFilterDirective(crossClassEventsIds)
                );
            });
        },
        [store, entityId, crossClassEventsIds, controller]
    );

    useEffect(
        function loadMockPinnedEntries() {
            if (rollOutCadNarrativeFilteringAndFocusImprovementsEnabled && currentUserId) {
                // TODO: RND-53331 replace with real bootstrap call (isPinned=true on /narrative/list)
                // store.setPinnedEntries(createMockPinnedEntries(currentUserId));
            }
            return () => {
                store.setPinnedEntries([]);
            };
        },
        [rollOutCadNarrativeFilteringAndFocusImprovementsEnabled, currentUserId, store]
    );

    if (infosListenerRef.current === undefined) {
        updateStreamListeners();
    }

    useStream(infosListenerRef.current);

    useStream(boloInfosListenerRef.current);

    const socketListenerRef = useRef<SocketListener<StreamMap>>();

    if (socketListenerRef.current === undefined) {
        socketListenerRef.current = {
            invokeOnInitialize: true,
            onSocketStatusChange: ({ status }) => {
                if (status === 'CONNECTED') {
                    if (!store.initialSocketConnectionMade) {
                        store.setInitialSocketConnectionMade(true);
                    }
                    fetchNextPage({ reset: true });
                }
            },
        };
    }

    useSocketStatus(socketListenerRef.current);
    // End of Realtime Update logic

    // Start of Unread Infos Logic
    const markAsRead = async (infos: AdditionalInfoView[], immediate = false): Promise<void> => {
        if (!immediate) {
            await sleep(MARK_AS_READ_DELAY_MS);
        }
        store.clearUnreadsBasedOnOldestUnread(infos);
    };

    const managerToUse: AdditionalInfoNotificationManager = notificationManager ?? {
        hasUnread: store.hasUnread,
        unreadInfos: store.unreadInfos,
        earliestUnreadInfo: store.earliestUnreadInfo,
        markAsRead,
    };

    return {
        ...managerToUse,
        entityId,
        entityNumber,
        agencyEventNumberToIdMap: store.agencyEventNumberToIdMap,
        createAdditionalInfo,
        deleteInfo,
        editorState: store.editorState,
        fetchNextPage,
        filter: store.filter,
        getIndexById: store.listStore.getItemIndex.bind(store.listStore),
        hasNextPage: store.hasNextPage,
        hiddenPinnedCount: store.hiddenPinnedCount,
        initialSocketConnectionMade: store.initialSocketConnectionMade,
        isFetchingNextPage: store.isFetchingNextPage,
        isHighPriority: store.isHighPriority,
        isPageReloading: store.isPageReloading,
        isPinnedSectionExpanded: store.isPinnedSectionExpanded,
        list: store.listStore.data.list,
        markAsUnread: store.markAsUnread,
        pageFetchFailed: store.pageFetchFailed,
        pageSize,
        reloadPage,
        retryPageFetch,
        setFilter,
        setIsHighPriority: store.setIsHighPriority,
        setSortDirection,
        setPinnedEntries: store.setPinnedEntries,
        showPinnedDivider: store.showPinnedDivider,
        sortDirection: store.sortDirection,
        storeEditorState: store.storeEditorState,
        togglePinnedSection: store.togglePinnedSection,
        visiblePinnedEntries: store.visiblePinnedEntries,
    };
};

// AdditionalInfosContext, useAdditionalInfos, and AdditionalInfosStoreProvider
// are in ./additional-infos-provider.tsx — import directly from there to avoid
// circular dependencies.
